'use client';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  listAdminRoles,
  listAdminPermissions,
  getUserRoles,
  updateUserRoles,
  updateAdminRolePermission,
  type UpdateUserRolesResult,
} from '@nomiss/services';

import { adminUsersQueryKeys } from './use-admin-users';

export const adminRolesQueryKeys = {
  all: ['admin', 'roles'] as const,
  list: () => [...adminRolesQueryKeys.all, 'list'] as const,
  permissions: () => [...adminRolesQueryKeys.all, 'permissions'] as const,
  userRoles: (userId: string) =>
    [...adminRolesQueryKeys.all, 'user-roles', userId] as const,
};

export function useAdminRoles(options?: { enabled?: boolean }) {
  const adminRolesQuery = useQuery({
    queryKey: adminRolesQueryKeys.list(),
    queryFn: () => listAdminRoles(),
    enabled: options?.enabled ?? true,
  });

  return { adminRolesQuery };
}

export function useAdminPermissions(options?: { enabled?: boolean }) {
  const adminPermissionsQuery = useQuery({
    queryKey: adminRolesQueryKeys.permissions(),
    queryFn: () => listAdminPermissions(),
    enabled: options?.enabled ?? true,
  });

  return { adminPermissionsQuery };
}

export function useAdminUserRoles(userId: string) {
  const adminUserRolesQuery = useQuery({
    queryKey: adminRolesQueryKeys.userRoles(userId),
    queryFn: () => getUserRoles(userId),
    enabled: !!userId,
  });

  return { adminUserRolesQuery };
}

export function useUpdateAdminRolePermission() {
  const queryClient = useQueryClient();

  const updateAdminRolePermissionMutation = useMutation({
    mutationFn: ({
      permissionId,
      allocated,
    }: {
      permissionId: string;
      allocated: boolean;
    }) => updateAdminRolePermission(permissionId, allocated),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: adminRolesQueryKeys.all,
      });
    },
  });

  return { updateAdminRolePermissionMutation };
}

export function useUpdateUserRoles() {
  const queryClient = useQueryClient();

  const updateUserRolesMutation = useMutation({
    mutationFn: ({
      userId,
      roles,
    }: {
      userId: string;
      roles: string[];
    }) => updateUserRoles(userId, roles),
    onSuccess: (_data: UpdateUserRolesResult, variables) => {
      void queryClient.invalidateQueries({
        queryKey: adminRolesQueryKeys.all,
      });
      void queryClient.invalidateQueries({
        queryKey: adminRolesQueryKeys.userRoles(variables.userId),
      });
      void queryClient.invalidateQueries({
        queryKey: adminUsersQueryKeys.detail(variables.userId),
      });
      void queryClient.invalidateQueries({
        queryKey: adminUsersQueryKeys.all,
      });
    },
  });

  return { updateUserRolesMutation };
}
